import React, { useEffect, useId, useRef, useState } from "react";
import { ScaleLoader } from "react-spinners";
import AccessibleDialog from "../UI/Modals/AccessibleDialog/AccessibleDialog";
import NotificationMessage from "../UI/NotificationMessage/NotificationMessage";
import PharmacyPicker from "./PharmacyPicker";
import { useConsultOrderContext } from "../../contexts/ConsultOrderContext";
import { ConsultOrderPhase } from "../../hooks/useConsultOrderLifecycle";
import {
  ConsultRfzoValidationErrors,
  RFZO_PHARMACY_NO_RFZO_ERROR,
  RFZO_PHARMACY_UNKNOWN_NOTICE,
} from "../../utils/consultRfzoPayload";
import { ConsultRfzoTherapyResponse } from "../../types/consultRfzo.types";
import styles from "./ConsultRfzoFormModal.module.scss";

// The consult RFZO request modal (ITAM-2887 advisor portal). The advisor is
// not a pharmacist: they only raise an EMPTY request with the pharmacy the
// caller chose, and the pharmacy fills it in later. Two steps over the shared
// ConsultOrderContext rfzoDraft:
//   RFZO_FORM / RFZO_SUBMITTING -> the entry form (pharmacy, LBO/JMBG,
//   patient name and phone, note, consent; submit = POST /rfzo_therapy),
//   RFZO_RESULT -> the 201 confirmation; the ONLY action is closing.

const FIELD_FOCUS_ORDER: (keyof ConsultRfzoValidationErrors)[] = [
  "pharmacy",
  "lbo",
  "jmbg",
  "firstName",
  "secondName",
  "consent",
];

const focusFirstInvalidField = (
  errors: ConsultRfzoValidationErrors,
  container: HTMLElement | null
): void => {
  const scope: ParentNode = container ?? document;
  for (const key of FIELD_FOCUS_ORDER) {
    if (!errors[key]) {
      continue;
    }
    const el = scope.querySelector<HTMLElement>(
      `[data-consult-field="${key}"]`
    );
    if (el) {
      // Some markers wrap a control rather than being one (the picked
      // pharmacy card). Focus the real control inside, so the advisor lands
      // on something named and visibly focused instead of a bare div.
      const control = el.matches("input, select, textarea, button")
        ? el
        : el.querySelector<HTMLElement>("input, select, textarea, button") ?? el;
      control.focus();
      if (typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ block: "center", behavior: "smooth" });
      }
      return;
    }
  }
};

export interface ConsultRfzoFormModalProps {
  isOpen: boolean;
  phase: ConsultOrderPhase;
  graceActive: boolean;
  consultEnded: boolean;
  rfzoResult: ConsultRfzoTherapyResponse | null;
  submitError: string | null;
  validationErrors: ConsultRfzoValidationErrors;
  /** Bumped on each failed submit attempt; drives focus to the first invalid field. */
  submitAttemptId: number;
  onSubmit: () => void;
  onClose: () => void;
  onCloseResult: () => void;
  onDismissError: () => void;
}

const TITLE_ID = "consult-rfzo-form-title";

const ConsultRfzoFormModal: React.FC<ConsultRfzoFormModalProps> = ({
  isOpen,
  phase,
  graceActive,
  consultEnded,
  rfzoResult,
  submitError,
  validationErrors,
  submitAttemptId,
  onSubmit,
  onClose,
  onCloseResult,
  onDismissError,
}) => {
  const {
    prefill,
    rfzoDraft,
    updateRfzoDraft,
    setRfzoPharmacy,
    patchRfzoPharmacy,
  } = useConsultOrderContext();

  const bodyRef = useRef<HTMLDivElement>(null);
  const resultCloseRef = useRef<HTMLButtonElement>(null);
  const copyTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Identity of the LATEST copy attempt in the CURRENT open session. Every
  // attempt claims a new generation and a modal close bumps it too, so a
  // stale settle can never write feedback, not even after a quick reopen,
  // which an "is the modal open now?" check would wave through.
  const copyGenRef = useRef(0);

  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "failed">(
    "idle"
  );

  const reactId = useId();
  const lboId = `${reactId}-lbo`;
  const jmbgId = `${reactId}-jmbg`;
  const firstNameId = `${reactId}-first-name`;
  const secondNameId = `${reactId}-second-name`;
  const phoneId = `${reactId}-phone`;
  const noteId = `${reactId}-note`;
  const consentId = `${reactId}-consent`;
  const consentErrId = `${consentId}-error`;
  const lboErrId = `${lboId}-error`;
  const jmbgErrId = `${jmbgId}-error`;
  const firstNameErrId = `${firstNameId}-error`;
  const secondNameErrId = `${secondNameId}-error`;

  const callerName =
    [prefill?.user.firstName, prefill?.user.secondName]
      .filter((part) => part && part.trim())
      .join(" ")
      .trim() ||
    prefill?.user.username ||
    "korisnika";

  const isResult = phase === "RFZO_RESULT";
  const isSubmitting = phase === "RFZO_SUBMITTING";

  const prefillHadLbo = Boolean(prefill?.user.lbo?.trim());
  // Q16: a confirmed non-RFZO pharmacy blocks the submit; unknown only warns.
  const pharmacyNoRfzo = rfzoDraft.pharmacy?.haveRFZO === 0;
  const pharmacyRfzoUnknown =
    rfzoDraft.pharmacy != null && rfzoDraft.pharmacy.haveRFZO == null;

  // On a failed submit attempt (nonce bump), move focus to the first invalid field.
  useEffect(() => {
    if (submitAttemptId <= 0) {
      return;
    }
    focusFirstInvalidField(validationErrors, bodyRef.current);
    // Re-focus once per attempt only, never while the advisor is editing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submitAttemptId]);

  // The FORM/SUBMITTING -> RESULT swap happens with the modal already open
  // (react-modal only applies initialFocusRef on the open transition), so move
  // focus to the result's primary action here (same pattern as the order form).
  useEffect(() => {
    if (isResult) {
      resultCloseRef.current?.focus();
    }
  }, [isResult]);

  // Leaving the form (or unmounting) drops transient UI state so a later
  // reopen starts clean; the DRAFT itself lives in the context and survives.
  useEffect(() => {
    if (!isOpen) {
      copyGenRef.current += 1; // invalidate pending clipboard settles
      setCopyStatus("idle");
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
        copyTimerRef.current = null;
      }
    }
  }, [isOpen]);

  useEffect(
    () => () => {
      copyGenRef.current += 1; // a settle after unmount must not set state
      if (copyTimerRef.current) {
        clearTimeout(copyTimerRef.current);
      }
    },
    []
  );

  const copyLbo = async () => {
    // "Kopiran" is only claimed once the write RESOLVES: clipboard access can
    // be denied (permission, insecure context, missing API), and the advisor
    // would otherwise paste nothing into the eApoteka portal while holding a
    // confirmation that says it worked. Trimmed to match what the submit
    // actually sends.
    const gen = ++copyGenRef.current;
    // A new attempt must not keep showing the PREVIOUS attempt's feedback
    // while its own write is pending: "LBO kopiran!" from value A reads as
    // confirmation of the just-clicked copy of value B.
    setCopyStatus("idle");
    if (copyTimerRef.current) {
      clearTimeout(copyTimerRef.current);
      copyTimerRef.current = null;
    }
    let status: "copied" | "failed" = "copied";
    try {
      await navigator.clipboard.writeText(rfzoDraft.lbo.trim());
    } catch {
      status = "failed";
    }
    if (gen !== copyGenRef.current) {
      return; // superseded by a newer attempt or a modal close
    }
    setCopyStatus(status);
    if (copyTimerRef.current) {
      clearTimeout(copyTimerRef.current);
    }
    copyTimerRef.current = setTimeout(() => {
      setCopyStatus("idle");
    }, 5000);
  };

  const handleRequestClose = () => {
    if (isResult) {
      onCloseResult();
    } else {
      onClose();
    }
  };

  const graceBanner = (consultEnded || graceActive) && (
    <div className={styles.graceBanner} role="status">
      Konsultacija je završena, RFZO upit možete poslati još kratko.
    </div>
  );

  const renderForm = () => (
    <>
      {graceBanner}
      {submitError && (
        <div className={styles.errorBanner} role="alert">
          <span className={styles.errorText}>{submitError}</span>
          <button
            type="button"
            className={styles.errorDismiss}
            onClick={onDismissError}
            aria-label="Zatvori poruku o grešci"
          >
            Zatvori
          </button>
        </div>
      )}

      <div className={styles.body} ref={bodyRef}>
        <section className={styles.section} aria-label="Apoteka">
          <h3 className={styles.sectionTitle}>Apoteka</h3>
          <PharmacyPicker
            value={rfzoDraft.pharmacy}
            onChange={setRfzoPharmacy}
            onDetail={patchRfzoPharmacy}
            hasItems={false}
            error={validationErrors.pharmacy}
            disabled={isSubmitting}
          />
          {pharmacyNoRfzo && !validationErrors.pharmacy && (
            <span className={styles.fieldError} role="alert">
              {RFZO_PHARMACY_NO_RFZO_ERROR}
            </span>
          )}
          {pharmacyRfzoUnknown && (
            <span className={styles.hint} role="note">
              {RFZO_PHARMACY_UNKNOWN_NOTICE}
            </span>
          )}
        </section>

        <section className={styles.section} aria-label="LBO i JMBG korisnika">
          <h3 className={styles.sectionTitle}>LBO i JMBG korisnika</h3>
          <p className={styles.sectionNote}>
            Unesite LBO ili JMBG korisnika (bar jedno je obavezno).
          </p>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={lboId}>
              LBO
            </label>
            <input
              id={lboId}
              className={styles.input}
              data-consult-field="lbo"
              type="text"
              value={rfzoDraft.lbo}
              onChange={(e) => updateRfzoDraft({ lbo: e.target.value })}
              aria-invalid={validationErrors.lbo ? true : undefined}
              aria-describedby={validationErrors.lbo ? lboErrId : undefined}
            />
            {validationErrors.lbo && (
              <span id={lboErrId} className={styles.fieldError} role="alert">
                {validationErrors.lbo}
              </span>
            )}
            {!prefillHadLbo && (
              <span className={styles.hint}>
                Korisnik nema sačuvan LBO. Unesite LBO ili JMBG ručno.
              </span>
            )}
          </div>
          {/* The eApoteka portal lookup keys on the LBO only, so a JMBG-only
              patient can never enable this action (ITAM-2716). */}
          <button
            type="button"
            className={styles.copyButton}
            onClick={copyLbo}
            disabled={!rfzoDraft.lbo.trim()}
          >
            Kopiraj LBO i na portalu eApoteka proveri dostupnost terapije
          </button>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={jmbgId}>
              JMBG
            </label>
            <input
              id={jmbgId}
              className={styles.input}
              data-consult-field="jmbg"
              type="text"
              inputMode="numeric"
              maxLength={13}
              value={rfzoDraft.jmbg}
              onChange={(e) => updateRfzoDraft({ jmbg: e.target.value })}
              aria-invalid={validationErrors.jmbg ? true : undefined}
              aria-describedby={validationErrors.jmbg ? jmbgErrId : undefined}
            />
            {validationErrors.jmbg && (
              <span id={jmbgErrId} className={styles.fieldError} role="alert">
                {validationErrors.jmbg}
              </span>
            )}
          </div>
        </section>

        <section className={styles.section} aria-label="Podaci korisnika">
          <h3 className={styles.sectionTitle}>Podaci korisnika</h3>
          <p className={styles.sectionNote}>
            Ovi podaci se prosleđuju apoteci uz RFZO upit.
          </p>
          <div className={styles.fieldGrid}>
            <div className={styles.field}>
              <label className={styles.label} htmlFor={firstNameId}>
                Ime
              </label>
              <input
                id={firstNameId}
                className={styles.input}
                data-consult-field="firstName"
                type="text"
                value={rfzoDraft.firstName}
                onChange={(e) =>
                  updateRfzoDraft({ firstName: e.target.value })
                }
                aria-invalid={validationErrors.firstName ? true : undefined}
                aria-describedby={
                  validationErrors.firstName ? firstNameErrId : undefined
                }
              />
              {validationErrors.firstName && (
                <span
                  id={firstNameErrId}
                  className={styles.fieldError}
                  role="alert"
                >
                  {validationErrors.firstName}
                </span>
              )}
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor={secondNameId}>
                Prezime
              </label>
              <input
                id={secondNameId}
                className={styles.input}
                data-consult-field="secondName"
                type="text"
                value={rfzoDraft.secondName}
                onChange={(e) =>
                  updateRfzoDraft({ secondName: e.target.value })
                }
                aria-invalid={validationErrors.secondName ? true : undefined}
                aria-describedby={
                  validationErrors.secondName ? secondNameErrId : undefined
                }
              />
              {validationErrors.secondName && (
                <span
                  id={secondNameErrId}
                  className={styles.fieldError}
                  role="alert"
                >
                  {validationErrors.secondName}
                </span>
              )}
            </div>
            <div className={styles.field}>
              <label className={styles.label} htmlFor={phoneId}>
                Broj telefona
              </label>
              <input
                id={phoneId}
                className={styles.input}
                type="tel"
                value={rfzoDraft.userPhone}
                onChange={(e) =>
                  updateRfzoDraft({ userPhone: e.target.value })
                }
              />
            </div>
          </div>
          <div className={styles.field}>
            <label className={styles.label} htmlFor={noteId}>
              Napomena za apoteku
            </label>
            <textarea
              id={noteId}
              className={styles.textarea}
              rows={2}
              value={rfzoDraft.note}
              onChange={(e) => updateRfzoDraft({ note: e.target.value })}
            />
            <span className={styles.hint}>
              Opciono. Apoteka vidi napomenu uz upit.
            </span>
          </div>
        </section>

        <section className={styles.section} aria-label="Saglasnost korisnika">
          <h3 className={styles.sectionTitle}>Saglasnost korisnika</h3>
          <div className={styles.consentRow}>
            <input
              id={consentId}
              className={styles.consentCheckbox}
              data-consult-field="consent"
              type="checkbox"
              checked={rfzoDraft.consent}
              onChange={(e) => updateRfzoDraft({ consent: e.target.checked })}
              aria-invalid={validationErrors.consent ? true : undefined}
              aria-describedby={
                validationErrors.consent ? consentErrId : undefined
              }
            />
            <label className={styles.consentLabel} htmlFor={consentId}>
              Korisnik je dao saglasnost da se njegov JMBG prosledi apoteci radi
              provere RFZO terapije.
            </label>
          </div>
          {validationErrors.consent && (
            <span id={consentErrId} className={styles.fieldError} role="alert">
              {validationErrors.consent}
            </span>
          )}
        </section>
      </div>

      <footer className={styles.footer}>
        <div className={styles.footerActions}>
          <button type="button" className={styles.secondary} onClick={onClose}>
            Otkaži
          </button>
          <button
            type="button"
            className={styles.primary}
            disabled={isSubmitting || pharmacyNoRfzo}
            onClick={() => onSubmit()}
          >
            {isSubmitting ? (
              <span className={styles.submitSpinner}>
                <ScaleLoader color="#ffffff" height={14} width={3} />
              </span>
            ) : (
              "Pošalji RFZO upit"
            )}
          </button>
        </div>
      </footer>
    </>
  );

  // A view-only confirmation: the request is the whole of the advisor's part
  // (no rerun, no order). Reopening via the prompt ("Pregled RFZO upita")
  // shows this screen again.
  const renderResult = () =>
    rfzoResult ? (
      <div className={styles.resultBody} role="status">
        <p className={styles.resultHeadline}>RFZO upit je poslat.</p>
        <p className={styles.resultText}>
          Upit je prosleđen izabranoj apoteci. Apoteka će dopuniti terapiju i
          korisnik će biti obavešten.
        </p>
        <div className={styles.footerActions}>
          <button
            type="button"
            ref={resultCloseRef}
            className={styles.primary}
            onClick={onCloseResult}
          >
            Zatvori
          </button>
        </div>
      </div>
    ) : null;

  return (
    <AccessibleDialog
      isOpen={isOpen}
      onRequestClose={handleRequestClose}
      titleId={TITLE_ID}
      maxWidth={760}
      className={styles.dialogInner}
    >
      <div className={styles.container}>
        <header className={styles.header}>
          <h2 id={TITLE_ID} className={styles.title}>
            RFZO upit: {callerName}
          </h2>
          {/* The account the request is attached to: every notification goes
              to this account, so the advisor must be able to see it before
              submitting (a shared family account is not always the person on
              the call). */}
          <p className={styles.callerMeta}>
            {[
              prefill?.user.phoneNumber?.trim(),
              prefill?.user.username?.trim(),
            ]
              .filter(Boolean)
              .join(" · ") || "Nema kontakt podataka"}
          </p>
        </header>
        {/* Rendered INSIDE the dialog: a page-level toast would sit behind
            this modal's overlay (z-index 80 vs 1000) and inside the
            aria-hidden app root, so the copy confirmation would be both
            invisible and unannounced. */}
        {copyStatus !== "idle" && (
          <NotificationMessage
            className={
              copyStatus === "failed"
                ? `${styles.copyToast} ${styles.copyToastFailed}`
                : styles.copyToast
            }
            role={copyStatus === "failed" ? "alert" : "status"}
          >
            {copyStatus === "copied"
              ? "LBO kopiran!"
              : "Kopiranje LBO nije uspelo, kopirajte ga ručno."}
          </NotificationMessage>
        )}
        {isResult ? renderResult() : renderForm()}
      </div>
    </AccessibleDialog>
  );
};

export default ConsultRfzoFormModal;
